/**
 * Founder Dashboard — single-pane weekly ops health view.
 *
 * Consolidates trial funnel, MRR breakdown, cold-outreach performance,
 * nurture A/B leaders, referral leaderboard, and at-risk trials into one
 * scrollable admin page so the founder knows exactly what to fix each
 * Monday without opening 5 admin tabs.
 *
 * Feeds off `GET /api/admin/founder-kpi/data` (JSON, admin-only).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChartLineUp, TrendUp, Users, Envelope, Trophy, WarningCircle, CurrencyDollar,
  Sparkle, ArrowRight, PaperPlaneTilt, Lock, ChatCircleText, PencilSimple, X,
  ImageSquare,
} from "@phosphor-icons/react";
import client from "../lib/api";

function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
function fmtDelta(current, prev) {
  if (!prev) return current > 0 ? `+${current}` : "0";
  const pct = ((current - prev) / prev) * 100;
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(0)}% wow`;
}

const StatCard = ({ icon: Icon, label, value, sub, tone = "neutral", testid }) => {
  const toneCls = {
    good: "border-emerald-400/30 bg-emerald-400/[0.03]",
    warn: "border-amber-400/30 bg-amber-400/[0.03]",
    bad:  "border-red-500/30 bg-red-500/[0.03]",
    hero: "border-[#FF5A1F]/30 bg-[#FF5A1F]/[0.03]",
    neutral: "border-white/10 bg-white/[0.02]",
  }[tone];
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-4 ${toneCls}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">
        <Icon size={12} weight="bold" /> {label}
      </div>
      <div className="font-display text-3xl font-semibold tracking-tight text-white">
        {value}
      </div>
      {sub && (
        <div className="text-[11px] text-neutral-400 mt-1.5 tabular-nums">{sub}</div>
      )}
    </div>
  );
};

export default function FounderDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // Which heatmap row is mid-SMS. Keyed by token so multiple rows can
  // be in-flight without stepping on each other.
  const [smsBusyToken, setSmsBusyToken] = useState(null);
  // Custom-message modal: `null` = closed, `{pilot}` = open pre-filled
  // with the default draft for that pilot.
  const [smsEditor, setSmsEditor] = useState(null);
  // Refresh trigger — after a successful SMS we bump this so the row's
  // "Sent ✓" state can render from the latest data payload.
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/founder-kpi/data");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load KPI data");
    } finally {
      setLoading(false);
    }
  };

  // Client-side default draft — mirrors backend `_default_thank_you_message`
  // so the modal's pre-fill matches what the "one-tap" button would send.
  const defaultThanksDraft = (pilot) => {
    const first = (pilot?.person_name || "").split(" ")[0].trim() || "there";
    const scans = Number(pilot?.scans || 0);
    const scanTxt =
      scans <= 1 ? "sticker's getting scans" : `sticker got ${scans} scans`;
    return (
      `Hey ${first} — Mike from FreightPilot AI. Your ${scanTxt} this week — ` +
      "genuinely appreciate you reppin' us. Text me back if there's anything I " +
      "can do to make FreightPilot better for your operation. 🚛"
    );
  };

  const sendThankYouSms = async (pilot, overrides = null) => {
    if (smsBusyToken) return;
    setSmsBusyToken(pilot.token);
    try {
      const payload = {};
      if (overrides?.message) payload.message = overrides.message;
      if (overrides?.phone_override) payload.phone_override = overrides.phone_override;
      const force = overrides?.force ? "?force=true" : "";
      const { data: res } = await client.post(
        `/admin/pilot-quote-invites/${pilot.token}/thank-you-sms${force}`,
        payload,
      );
      if (res.status === "sent") {
        toast.success(`Thanks-SMS sent to ${pilot.person_name} (${res.phone})`);
        setSmsEditor(null);
        await load();
      } else if (res.status === "already_sent") {
        toast.info(
          `Already thanked ${pilot.person_name} — hit Edit + Force to send again`,
        );
      } else if (res.status === "twilio_error") {
        toast.error(`Twilio said no: ${res.reason?.slice(0, 100) || "unknown"}`);
      } else {
        toast.error("Unexpected response from thank-you endpoint");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Thanks-SMS failed — see console",
      );
    } finally {
      setSmsBusyToken(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      await client.post("/admin/founder-kpi/send");
      toast.success("Digest sent to your inbox");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 sm:p-8" data-testid="founder-dashboard-loading">
        <div className="text-sm text-neutral-500">Loading weekly ops snapshot…</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-6 sm:p-8">
        <div className="text-sm text-red-400">Failed to load — refresh?</div>
      </div>
    );
  }

  const s = data.signups;
  const m = data.mrr;
  const o = data.outreach;

  return (
    <div className="p-6 sm:p-8 space-y-8" data-testid="founder-dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold mb-1">
            Weekly Ops Snapshot
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Founder Dashboard
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Week of <span className="text-neutral-300 font-medium">{data.week_of}</span>
            {" · "}
            <span className="text-neutral-600">
              generated {new Date(data.generated_at).toLocaleString()}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="founder-dashboard-refresh"
            className="text-xs uppercase tracking-widest text-neutral-500 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-3 py-1.5 transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={sendNow}
            disabled={sending}
            data-testid="founder-dashboard-send"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5A1F] hover:bg-[#FF7038] text-black text-xs font-semibold px-3.5 py-1.5 disabled:opacity-60 transition-colors"
          >
            <PaperPlaneTilt size={12} weight="fill" />
            {sending ? "Sending…" : "Email me the digest"}
          </button>
        </div>
      </div>

      {/* Compact widget row — early signal on which share-card style is winning */}
      <ShareStyleWidget />

      {/* Waitlist signal — velocity + pending demand for cohort 2 */}
      <WaitlistSignalWidget />

      {/* Row 1 · TRIAL FUNNEL */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3 flex items-center gap-2">
          <ChartLineUp size={12} weight="bold" /> Trial Funnel · last 7 days
        </div>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <StatCard
            testid="stat-signups-7d"
            icon={Users}
            label="Signups (7d)"
            value={s.last_7d}
            sub={fmtDelta(s.last_7d, s.prev_7d)}
            tone={s.last_7d > s.prev_7d ? "good" : "neutral"}
          />
          <StatCard
            testid="stat-activated-7d"
            icon={Sparkle}
            label="Activated (7d)"
            value={s.activated_7d}
            sub={`${s.activation_rate_pct}% activation rate`}
            tone={s.activation_rate_pct >= 30 ? "good" : s.activation_rate_pct >= 15 ? "warn" : "bad"}
          />
          <StatCard
            testid="stat-total-users"
            icon={Users}
            label="Total users"
            value={s.total.toLocaleString()}
            sub={`${m.trialing} on trial · ${m.active_subs} paid`}
          />
          <StatCard
            testid="stat-at-risk"
            icon={WarningCircle}
            label="At-risk trials"
            value={data.at_risk_trials.length}
            sub="5+ days since login"
            tone={data.at_risk_trials.length > 5 ? "bad" : data.at_risk_trials.length > 0 ? "warn" : "good"}
          />
        </div>
      </section>

      {/* Row 2 · MRR */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3 flex items-center gap-2">
          <CurrencyDollar size={12} weight="bold" /> Recurring Revenue
        </div>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <StatCard
            testid="stat-mrr"
            icon={CurrencyDollar}
            label="Current MRR"
            value={fmtMoney(m.current)}
            sub={`${m.active_subs} active subs`}
            tone="hero"
          />
          <StatCard
            testid="stat-new-mrr"
            icon={TrendUp}
            label="New MRR this week"
            value={fmtMoney(m.new_this_week)}
            tone={m.new_this_week > 0 ? "good" : "neutral"}
          />
          <StatCard
            testid="stat-churn-mrr"
            icon={TrendUp}
            label="Churned this week"
            value={fmtMoney(m.churned_this_week)}
            tone={m.churned_this_week > 0 ? "bad" : "neutral"}
          />
          <StatCard
            testid="stat-net-mrr"
            icon={TrendUp}
            label="Net movement"
            value={`${m.net_this_week >= 0 ? "+" : ""}${fmtMoney(m.net_this_week)}`}
            tone={m.net_this_week >= 0 ? "good" : "bad"}
          />
        </div>
      </section>

      {/* Row 3 · COLD OUTREACH + NURTURE A/B side by side */}
      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-4 flex items-center gap-2">
            <Envelope size={12} weight="bold" /> Cold Outreach · last 7d
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <div className="text-xs text-neutral-500">Sent</div>
              <div className="text-2xl font-semibold tabular-nums">{o.sent_7d.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">Total ever</div>
              <div className="text-2xl font-semibold text-neutral-400 tabular-nums">{o.sent_total.toLocaleString()}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-white/5">
            <div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-widest">Open rate</div>
              <div className="text-lg font-semibold text-neutral-200 tabular-nums">{o.open_rate_pct}%</div>
            </div>
            <div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-widest">Click rate</div>
              <div className="text-lg font-semibold text-neutral-200 tabular-nums">{o.click_rate_pct}%</div>
            </div>
            <div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-widest">Reply rate</div>
              <div className={`text-lg font-semibold tabular-nums ${o.reply_rate_pct >= 2 ? "text-emerald-400" : "text-neutral-200"}`}>
                {o.reply_rate_pct}%
              </div>
            </div>
          </div>
          <div className="text-[11px] text-neutral-500 mt-3">
            <span className="text-neutral-400">{o.followups_queued}</span> follow-ups queued
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="section-nurture-ab">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-4 flex items-center gap-2">
            <Sparkle size={12} weight="bold" /> Nurture A/B leaders
          </div>
          {data.nurture_ab.locked_winners.length === 0 ? (
            <div className="text-sm text-neutral-500 py-8 text-center">
              No locked winners yet. Auto-lock kicks in after 30 days of data + a 5pp gap.
            </div>
          ) : (
            <div className="space-y-2">
              {data.nurture_ab.locked_winners.map((w) => (
                <div key={w.step_key} className="flex items-start gap-3 rounded-md border border-[#FF5A1F]/20 bg-[#FF5A1F]/[0.04] p-3">
                  <Lock size={12} weight="fill" className="text-[#FF9764] mt-1 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold mb-0.5">
                      {w.step_key} · Variant {String.fromCharCode(65 + w.variant_index)} · locked by {w.locked_by}
                    </div>
                    <div className="text-sm text-neutral-200 leading-snug">"{w.subject}"</div>
                    {w.winner_open_rate_pct != null && (
                      <div className="text-[10px] text-neutral-500 mt-1 tabular-nums">
                        {w.winner_open_rate_pct}% open vs {w.loser_open_rate_pct}% loser
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Row 4 · REFERRAL LEADERBOARD + AT-RISK TRIALS side by side */}
      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="section-referral-leaders">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-4 flex items-center gap-2">
            <Trophy size={12} weight="bold" /> Top referrers
          </div>
          {data.referral_leaders.length === 0 ? (
            <div className="text-sm text-neutral-500 py-8 text-center">
              No confirmed referrals yet.
            </div>
          ) : (
            <div className="space-y-2">
              {data.referral_leaders.map((r, i) => (
                <div key={r.email} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                  <div className={`text-xs font-bold w-6 shrink-0 text-center ${
                    i === 0 ? "text-[#FF9764]" : i === 1 ? "text-neutral-300" : i === 2 ? "text-amber-700" : "text-neutral-500"
                  }`}>#{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{r.name}</div>
                    <div className="text-[10px] text-neutral-500 truncate">{r.email}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold tabular-nums">{r.referrals}</div>
                    <div className="text-[10px] text-neutral-500 tabular-nums">+{r.days_earned}d earned</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="section-at-risk-trials">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-4 flex items-center gap-2">
            <WarningCircle size={12} weight="bold" /> At-risk trials
          </div>
          {data.at_risk_trials.length === 0 ? (
            <div className="text-sm text-neutral-500 py-8 text-center">
              Nobody's ghosting — every trial is warm.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {data.at_risk_trials.map((u) => (
                <div key={u.email} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="text-neutral-200 truncate">{u.name}</div>
                    <div className="text-[10px] text-neutral-500 truncate">{u.email}</div>
                  </div>
                  <div className="text-right shrink-0 tabular-nums text-neutral-500 text-[10px]">
                    <div>joined {u.signed_up}</div>
                    <div>last seen {u.last_seen}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Row 4.5 · Pilot bookings funnel */}
      {data.bookings && (
        <section
          data-testid="founder-bookings-section"
          className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-[#FF9764] mb-1">Pilot Bookings · Last 7 days</div>
              <h3 className="font-display text-lg font-semibold">Discovery-call funnel</h3>
            </div>
            <a
              href="/app/admin/pilot-drip"
              className="text-[11px] text-neutral-500 hover:text-[#FF9764] inline-flex items-center gap-1"
            >
              Open Drip admin <ArrowRight size={11} weight="bold" />
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <BookingsStat label="Booked" value={data.bookings.booked_7d} testid="bookings-booked" />
            <BookingsStat label="Held" value={data.bookings.held_7d} accent="emerald" testid="bookings-held" />
            <BookingsStat
              label="Ghosted"
              value={data.bookings.noshow_7d}
              accent={data.bookings.noshow_7d > 0 ? "red" : "neutral"}
              testid="bookings-ghosted"
            />
            <BookingsStat label="Cancelled" value={data.bookings.cancelled_7d} testid="bookings-cancelled" />
            <BookingsStat label="Upcoming" value={data.bookings.upcoming} accent="orange" testid="bookings-upcoming" />
          </div>
          <div className="mt-3 flex items-center gap-2 text-[11px] text-neutral-500">
            Ghost rate:{" "}
            <span
              className={
                data.bookings.ghost_rate_pct >= 30
                  ? "text-red-300 font-semibold"
                  : data.bookings.ghost_rate_pct >= 15
                  ? "text-amber-300 font-semibold"
                  : "text-emerald-300 font-semibold"
              }
              data-testid="bookings-ghost-rate"
            >
              {data.bookings.ghost_rate_pct}%
            </span>
            <span className="text-neutral-600">· fires 4h after a booking passes without a "held" mark</span>
          </div>

          {/* Reschedule Attribution funnel — sent → clicked → converted */}
          <div className="mt-5 pt-5 border-t border-white/10" data-testid="rebook-funnel-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline text-neutral-500 mb-1">Reschedule attribution · Last 7 days</div>
                <div className="text-[13px] text-neutral-400">How well the no-show follow-up email recovers ghosted calls</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <BookingsStat
                label="No-show emails sent"
                value={data.bookings.rebook_sent_7d ?? 0}
                testid="rebook-sent"
              />
              <BookingsStat
                label="Rebook link clicked"
                value={data.bookings.rebook_clicked_7d ?? 0}
                accent="orange"
                testid="rebook-clicked"
              />
              <BookingsStat
                label="Converted (new booking)"
                value={data.bookings.rebook_converted_7d ?? 0}
                accent="emerald"
                testid="rebook-converted"
              />
            </div>
            <div className="mt-3 flex items-center flex-wrap gap-3 text-[11px] text-neutral-500">
              <span>
                Click rate:{" "}
                <span
                  className={
                    (data.bookings.rebook_click_rate_pct ?? 0) >= 25
                      ? "text-emerald-300 font-semibold"
                      : (data.bookings.rebook_click_rate_pct ?? 0) >= 10
                      ? "text-amber-300 font-semibold"
                      : "text-neutral-400 font-semibold"
                  }
                  data-testid="rebook-click-rate"
                >
                  {data.bookings.rebook_click_rate_pct ?? 0}%
                </span>
              </span>
              <span className="text-neutral-700">·</span>
              <span>
                Conversion rate:{" "}
                <span
                  className={
                    (data.bookings.rebook_conversion_rate_pct ?? 0) >= 10
                      ? "text-emerald-300 font-semibold"
                      : (data.bookings.rebook_conversion_rate_pct ?? 0) >= 3
                      ? "text-amber-300 font-semibold"
                      : "text-neutral-400 font-semibold"
                  }
                  data-testid="rebook-conversion-rate"
                >
                  {data.bookings.rebook_conversion_rate_pct ?? 0}%
                </span>
              </span>
              <span className="text-neutral-700">·</span>
              <span className="text-neutral-600">One-click link inside the "sorry we missed each other" email</span>
            </div>
            {(data.bookings.holds_extended_7d ?? 0) > 0 && (
              <div className="mt-3 pt-3 border-t border-white/5 flex items-center flex-wrap gap-3 text-[11px] text-neutral-500" data-testid="extension-save-row">
                <span className="uppercase tracking-widest text-[10px] text-[#FF9764] font-semibold">
                  Extension saves
                </span>
                <span>
                  Holds extended:{" "}
                  <span className="text-white font-semibold tabular-nums" data-testid="holds-extended">
                    {data.bookings.holds_extended_7d}
                  </span>
                </span>
                <span className="text-neutral-700">·</span>
                <span>
                  Converted after extension:{" "}
                  <span className="text-emerald-300 font-semibold tabular-nums" data-testid="holds-extended-converted">
                    {data.bookings.holds_extended_converted_7d ?? 0}
                  </span>
                </span>
                <span className="text-neutral-700">·</span>
                <span>
                  Save rate:{" "}
                  <span
                    className={
                      (data.bookings.extension_save_rate_pct ?? 0) >= 30
                        ? "text-emerald-300 font-semibold"
                        : (data.bookings.extension_save_rate_pct ?? 0) >= 10
                        ? "text-amber-300 font-semibold"
                        : "text-neutral-400 font-semibold"
                    }
                    data-testid="extension-save-rate"
                  >
                    {data.bookings.extension_save_rate_pct ?? 0}%
                  </span>
                </span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Row 4.75 · Cohort Health — recovery ROI on last N pilot emails */}
      {data.cohort_health && (
        <section
          data-testid="cohort-health-section"
          className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div>
              <div className="overline text-neutral-500 mb-1">Founding-pilot cohort health</div>
              <h3 className="font-display text-xl font-semibold">
                Last {data.cohort_health.cohort_size} pilot emails — live recovery pulse
              </h3>
              <p className="text-xs text-neutral-500 mt-1">
                Merged from nurture + onboarding drip. SMS-recovered = auto-fired Twilio nudges
                on hard-bounced pilots.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <BookingsStat
              label="Sent"
              value={data.cohort_health.sent}
              testid="cohort-sent"
              spark={data.cohort_health.sparkline?.sent_by_day}
              sparkTone="orange"
              sparkLabel="sent"
            />
            <BookingsStat
              label="Opened"
              value={data.cohort_health.opened}
              accent="orange"
              testid="cohort-opened"
            />
            <BookingsStat
              label="Bounced"
              value={data.cohort_health.bounced}
              accent={
                data.cohort_health.sparkline?.bounce_anomaly?.spike_detected
                  ? "red"
                  : data.cohort_health.bounced > 0
                  ? "red"
                  : "neutral"
              }
              testid="cohort-bounced"
              spark={data.cohort_health.sparkline?.bounced_by_day}
              sparkTone="red"
              sparkLabel="bounced"
              anomaly={data.cohort_health.sparkline?.bounce_anomaly}
            />
            <BookingsStat
              label="Replied"
              value={data.cohort_health.replied}
              accent="emerald"
              testid="cohort-replied"
            />
            <BookingsStat
              label="SMS recovered"
              value={data.cohort_health.sms_recovered}
              accent="emerald"
              testid="cohort-sms-recovered"
              spark={data.cohort_health.sparkline?.sms_recovered_by_day}
              sparkTone="emerald"
              sparkLabel="recovered"
            />
          </div>
          <div className="mt-3 flex items-center flex-wrap gap-3 text-[11px] text-neutral-500">
            <span>
              Open rate:{" "}
              <span
                className={
                  data.cohort_health.open_rate_pct >= 40
                    ? "text-emerald-300 font-semibold"
                    : data.cohort_health.open_rate_pct >= 20
                    ? "text-amber-300 font-semibold"
                    : "text-neutral-400 font-semibold"
                }
                data-testid="cohort-open-rate"
              >
                {data.cohort_health.open_rate_pct}%
              </span>
            </span>
            <span className="text-neutral-700">·</span>
            <span>
              Bounce rate:{" "}
              <span
                className={
                  data.cohort_health.bounce_rate_pct >= 5
                    ? "text-red-300 font-semibold"
                    : data.cohort_health.bounce_rate_pct >= 2
                    ? "text-amber-300 font-semibold"
                    : "text-emerald-300 font-semibold"
                }
                data-testid="cohort-bounce-rate"
              >
                {data.cohort_health.bounce_rate_pct}%
              </span>
            </span>
            <span className="text-neutral-700">·</span>
            <span>
              Reply rate:{" "}
              <span
                className={
                  data.cohort_health.reply_rate_pct >= 5
                    ? "text-emerald-300 font-semibold"
                    : "text-neutral-400 font-semibold"
                }
                data-testid="cohort-reply-rate"
              >
                {data.cohort_health.reply_rate_pct}%
              </span>
            </span>
          </div>
        </section>
      )}

      {/* Row 4.9 · Vinyl QR Scans — Truck-Show Dashboard */}
      {data.vinyl_scans && (
        <section
          data-testid="vinyl-scans-section"
          className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div>
              <div className="overline text-neutral-500 mb-1">Truck-show dashboard · UTM-tagged vinyl QR scans</div>
              <h3 className="font-display text-xl font-semibold">
                Vinyl QR scans · last 7 days
              </h3>
              <p className="text-xs text-neutral-500 mt-1">
                Every physical sticker's QR carries a{" "}
                <code className="text-neutral-400">utm_source=vinyl</code> tag —
                we count real-world scans against how many stickers you've ordered.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <BookingsStat
              label="Stickers ordered"
              value={data.vinyl_scans.ordered_vinyls}
              testid="vinyl-ordered"
            />
            <BookingsStat
              label="Scans (7d)"
              value={data.vinyl_scans.scans_7d}
              accent={data.vinyl_scans.scans_7d > 0 ? "orange" : "neutral"}
              testid="vinyl-scans-7d"
              spark={data.vinyl_scans.scans_by_day}
              sparkTone="orange"
              sparkLabel="scans"
            />
            <BookingsStat
              label="Unique pilots scanned"
              value={data.vinyl_scans.unique_tokens_scanned_7d}
              testid="vinyl-unique"
              accent={data.vinyl_scans.unique_tokens_scanned_7d > 0 ? "orange" : "neutral"}
            />
            <BookingsStat
              label="Scan rate"
              value={`${data.vinyl_scans.scan_rate_pct}%`}
              accent={
                data.vinyl_scans.scan_rate_pct >= 30
                  ? "emerald"
                  : data.vinyl_scans.scan_rate_pct >= 10
                  ? "orange"
                  : "neutral"
              }
              testid="vinyl-scan-rate"
            />
          </div>
          {data.vinyl_scans.top_campaigns_7d && data.vinyl_scans.top_campaigns_7d.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/5" data-testid="vinyl-top-campaigns">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
                Top events (by scans)
              </div>
              <div className="flex flex-wrap gap-2">
                {data.vinyl_scans.top_campaigns_7d.map((c) => (
                  <div
                    key={c.campaign}
                    className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/30 bg-[#FF5A1F]/[0.06] px-3 py-1 text-xs"
                    data-testid={`vinyl-campaign-${c.campaign}`}
                  >
                    <span className="text-neutral-300">{c.campaign}</span>
                    <span className="text-[#FF9764] font-semibold tabular-nums">{c.scans}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scan Attribution Heatmap — which specific pilot's sticker
              is earning its keep? Sorted desc by scan count so the top
              performer is always the first row Mike sees. */}
          {data.vinyl_scans.top_pilots_7d && data.vinyl_scans.top_pilots_7d.length > 0 && (
            <div
              className="mt-4 pt-3 border-t border-white/5"
              data-testid="vinyl-scan-heatmap"
            >
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                  Top pilots by scan count · last 7d
                </div>
                <div className="text-[10px] text-neutral-600">
                  DM the top performer a "thanks for the reps" note ↴
                </div>
              </div>
              <div className="overflow-hidden rounded-lg border border-white/10">
                <table className="w-full text-xs">
                  <thead className="bg-white/[0.03]">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500">
                      <th className="px-3 py-2 font-semibold">#</th>
                      <th className="px-3 py-2 font-semibold">Pilot</th>
                      <th className="px-3 py-2 font-semibold">Company</th>
                      <th className="px-3 py-2 font-semibold text-right">Scans</th>
                      <th className="px-3 py-2 font-semibold">Last scan</th>
                      <th className="px-3 py-2 font-semibold text-right">Thanks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.vinyl_scans.top_pilots_7d.map((p, idx) => {
                      // Simple color-scale: top gets the strongest orange,
                      // subsequent rows fade toward neutral. Formula caps
                      // at row 0 = full intensity, row 9 = ~10%.
                      const topScans = data.vinyl_scans.top_pilots_7d[0].scans || 1;
                      const intensity = Math.max(0.06, (p.scans / topScans) * 0.22);
                      const lastSeen = p.last_scan_at
                        ? new Date(p.last_scan_at).toLocaleString(undefined, {
                            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                          })
                        : "—";
                      const busy = smsBusyToken === p.token;
                      return (
                        <tr
                          key={p.token}
                          data-testid={`vinyl-heatmap-row-${idx}`}
                          className="border-t border-white/5"
                          style={{ backgroundColor: `rgba(255, 90, 31, ${intensity})` }}
                        >
                          <td className="px-3 py-2 text-neutral-500 tabular-nums font-semibold">
                            {idx === 0 ? (
                              <span className="text-[#FF9764]">#1</span>
                            ) : (
                              `#${idx + 1}`
                            )}
                          </td>
                          <td className="px-3 py-2 text-neutral-100 truncate max-w-[180px]">
                            {p.person_name}
                          </td>
                          <td className="px-3 py-2 text-neutral-400 truncate max-w-[180px]">
                            {p.company_name || <span className="text-neutral-600">—</span>}
                          </td>
                          <td
                            className="px-3 py-2 text-right tabular-nums font-semibold text-[#FF9764]"
                            data-testid={`vinyl-heatmap-row-${idx}-scans`}
                          >
                            {p.scans}
                          </td>
                          <td className="px-3 py-2 text-neutral-500 tabular-nums whitespace-nowrap">
                            {lastSeen}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="inline-flex items-center gap-1">
                              <button
                                onClick={() => setSmsEditor({ pilot: p })}
                                disabled={busy}
                                data-testid={`vinyl-heatmap-row-${idx}-thanks-edit-btn`}
                                className="inline-flex items-center rounded-full border border-white/15 hover:border-white/40 text-neutral-400 hover:text-white text-[10px] font-semibold w-6 h-6 justify-center transition-colors disabled:opacity-40"
                                title="Edit the message before sending"
                              >
                                <PencilSimple size={11} weight="bold" />
                              </button>
                              <button
                                onClick={() => sendThankYouSms(p)}
                                disabled={busy}
                                data-testid={`vinyl-heatmap-row-${idx}-thanks-btn`}
                                className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-400/[0.08] hover:bg-emerald-400/[0.15] text-emerald-200 text-[10px] font-semibold px-2.5 py-1 transition-colors disabled:opacity-40 disabled:cursor-wait"
                                title={`Send the pre-drafted "thanks for the reps" SMS to ${p.person_name}`}
                              >
                                <ChatCircleText size={11} weight="fill" />
                                {busy ? "Sending…" : "Send"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {data.vinyl_scans.ordered_vinyls > 0 && data.vinyl_scans.scans_7d === 0 && (
            <div className="mt-3 text-[11px] text-neutral-500" data-testid="vinyl-scans-none-yet">
              No scans yet — stickers still in transit or ops needs to hand them out at the next event.
            </div>
          )}
        </section>
      )}

      {/* Row 5 · Ops health footer */}
      <section className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between text-xs">
        <div className="text-neutral-500">
          Failed email sends (7d):{" "}
          <span className={data.ops.failed_email_sends_7d > 0 ? "text-red-400 font-semibold" : "text-emerald-400 font-semibold"}>
            {data.ops.failed_email_sends_7d}
          </span>
        </div>
        <a
          href="/app/admin/nurture"
          className="text-neutral-500 hover:text-[#FF9764] inline-flex items-center gap-1"
        >
          Open Nurture admin <ArrowRight size={11} weight="bold" />
        </a>
      </section>

      {/* Custom-message modal — tweak the pre-drafted SMS before sending
          to a heatmap top scanner. Closes on backdrop click / Cancel /
          successful send. */}
      {smsEditor && (
        <ThanksSmsEditor
          pilot={smsEditor.pilot}
          defaultDraft={defaultThanksDraft}
          onClose={() => setSmsEditor(null)}
          onSend={(overrides) => sendThankYouSms(smsEditor.pilot, overrides)}
          busy={smsBusyToken === smsEditor.pilot.token}
        />
      )}
    </div>
  );
}


/* ─── ThanksSmsEditor ───
   Small controlled modal that lets ops tweak the pre-drafted SMS,
   override the phone, and toggle a "force resend" flag before firing
   the same POST /admin/pilot-quote-invites/{token}/thank-you-sms endpoint
   the one-tap button uses. */
/* ─── SHARE STYLE WIDGET ─── */

/* Waitlist velocity widget — surfaces cohort-2 demand signal alongside
   the trial-funnel KPIs. Silent when the waitlist is empty (no forced
   "0 waitlist" box on a fresh install) — appears the moment the first
   cohort-full signup rolls in. */
function WaitlistSignalWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    client
      .get("/admin/waitlist/signal")
      .then((r) => {
        if (!cancelled) setData(r.data || null);
      })
      .catch(() => setData(null));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data || data.total === 0) return null;

  const {
    total, new_this_week, pending_invite, invited_7d, top_fleet_sizes = [],
  } = data;

  return (
    <section
      data-testid="waitlist-signal-widget"
      className="card-surface p-4 sm:p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-2">
            <Users size={12} weight="bold" /> Cohort 2 waitlist · velocity
          </div>
          <div className="text-sm text-neutral-400 mt-1">
            {pending_invite > 0
              ? `${pending_invite} pending — invite them from the waitlist page`
              : "All caught up — every waitlist entry has been invited"}
          </div>
        </div>
        <Link
          to="/app/admin/waitlist"
          data-testid="waitlist-widget-open-btn"
          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-white/10 hover:border-white/25 bg-white/[0.02] px-3 py-1.5 text-[11px] text-neutral-300 transition-colors"
        >
          Open waitlist <ArrowRight size={11} weight="bold" />
        </Link>
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        <div className="rounded-lg bg-white/[0.03] p-3">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">Total on list</div>
          <div
            data-testid="waitlist-widget-total"
            className="text-xl font-semibold text-white tabular-nums"
          >
            {total}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] p-3">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">New this week</div>
          <div
            data-testid="waitlist-widget-new"
            className="text-xl font-semibold text-[#FF9764] tabular-nums"
          >
            +{new_this_week}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] p-3">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">Pending invite</div>
          <div
            data-testid="waitlist-widget-pending"
            className={`text-xl font-semibold tabular-nums ${
              pending_invite > 0 ? "text-[#FF9764]" : "text-emerald-300"
            }`}
          >
            {pending_invite}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] p-3">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">Invited (7d)</div>
          <div
            data-testid="waitlist-widget-invited"
            className="text-xl font-semibold text-emerald-300 tabular-nums"
          >
            {invited_7d}
          </div>
        </div>
      </div>

      {top_fleet_sizes.length > 0 && (
        <div
          data-testid="waitlist-widget-fleet-mix"
          className="mt-3 pt-3 border-t border-white/5 flex flex-wrap items-center gap-2 text-[11px] text-neutral-400"
        >
          <span className="text-neutral-500">Fleet mix:</span>
          {top_fleet_sizes.map((f) => (
            <span
              key={f.label}
              className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.02] px-2 py-0.5"
            >
              <span className="tabular-nums text-white">{f.count}</span>
              <span className="text-neutral-500">·</span>
              <span>{f.label}</span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}



/* Compact dashboard widget that answers the question "which share-card
   style is my pilots' vibe?" at a glance. Powers a lightweight decision:
   if `holiday` is winning 60% of shares mid-December, we know the
   seasonal variant is doing its job.

   Feeds off GET /admin/share-card-stats — same endpoint the deeper
   analytics view uses. Silent when there's no data yet (fresh install)
   so the dashboard doesn't render an empty confusing box. */
function ShareStyleWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    client
      .get("/admin/share-card-stats")
      .then((r) => {
        if (cancelled) return;
        setStats(r.data || null);
      })
      .catch(() => setStats(null))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  const recent = stats?.recent_30d?.by_style || {};
  const total = Object.values(recent).reduce((a, b) => a + Number(b || 0), 0);
  if (total === 0) return null; // no data yet — hide instead of showing "0 shares"

  // Deterministic order matches the picker on AccountReferralPage
  const ordered = [
    { id: "bold", label: "Bold", color: "#FF5A1F", swatch: "bg-[#FF5A1F]" },
    { id: "minimalist", label: "Minimalist", color: "#E5E5E5", swatch: "bg-neutral-200" },
    { id: "holiday", label: "Holiday", color: "#DC2626", swatch: "bg-red-600" },
  ].map((s) => ({
    ...s,
    count: Number(recent[s.id] || 0),
    pct: total ? Math.round((100 * Number(recent[s.id] || 0)) / total) : 0,
  }));
  const leader = ordered.reduce((best, s) => (s.count > best.count ? s : best), ordered[0]);
  const leaderPct = leader.pct;

  return (
    <section data-testid="share-style-widget" className="card-surface p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5 flex items-center gap-1.5">
            <ImageSquare size={11} weight="fill" className="text-[#FF9764]" />
            Most-shared card · last 30 days
          </div>
          <div className="font-display text-xl sm:text-2xl font-semibold tracking-tight">
            <span style={{ color: leader.color }}>{leader.label}</span>
            <span className="text-neutral-400 font-normal ml-2">
              is winning {leaderPct}% of shares
            </span>
          </div>
          <div className="text-xs text-neutral-500 mt-1">
            {total} share{total === 1 ? "" : "s"} across{" "}
            {ordered.filter((s) => s.count > 0).length} style
            {ordered.filter((s) => s.count > 0).length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="w-full sm:max-w-md sm:min-w-[280px] space-y-1.5">
          {ordered.map((s) => (
            <div
              key={s.id}
              data-testid={`share-style-row-${s.id}`}
              className="flex items-center gap-3 text-xs"
            >
              <div className="flex items-center gap-1.5 w-24 shrink-0">
                <span className={`h-2.5 w-2.5 rounded-full ${s.swatch}`} />
                <span className="text-neutral-300">{s.label}</span>
              </div>
              <div className="relative flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-500"
                  style={{
                    width: `${Math.max(s.pct, s.count > 0 ? 4 : 0)}%`,
                    backgroundColor: s.color,
                  }}
                />
              </div>
              <div className="w-16 text-right text-neutral-400 tabular-nums">
                {s.count} · {s.pct}%
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}



function ThanksSmsEditor({ pilot, defaultDraft, onClose, onSend, busy }) {
  const [message, setMessage] = useState(() => defaultDraft(pilot));
  const [phoneOverride, setPhoneOverride] = useState("");
  const [force, setForce] = useState(false);
  const remaining = 320 - message.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="thanks-sms-editor-backdrop"
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0F0F0F] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="thanks-sms-editor"
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-white/5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold mb-1">
              Thank-you SMS · {pilot.scans} scan{pilot.scans === 1 ? "" : "s"}
            </div>
            <h4 className="font-display text-lg font-semibold text-white">
              Text {pilot.person_name}
              {pilot.company_name && (
                <span className="text-neutral-400 font-normal"> · {pilot.company_name}</span>
              )}
            </h4>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-white p-1"
            data-testid="thanks-sms-editor-close"
            aria-label="Close editor"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold block mb-1.5">
              Message body
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={320}
              data-testid="thanks-sms-editor-message"
              className="w-full rounded-lg bg-black/60 border border-white/10 focus:border-emerald-400/40 focus:outline-none px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 resize-none"
              placeholder="Write a warm one-line thanks…"
            />
            <div className="flex items-center justify-between mt-1">
              <button
                type="button"
                onClick={() => setMessage(defaultDraft(pilot))}
                className="text-[10px] text-neutral-500 hover:text-neutral-300 underline"
                data-testid="thanks-sms-editor-reset"
              >
                Reset to default
              </button>
              <span
                className={`text-[10px] tabular-nums ${
                  remaining < 20 ? "text-amber-300" : "text-neutral-600"
                }`}
                data-testid="thanks-sms-editor-char-count"
              >
                {remaining} chars left
              </span>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold block mb-1.5">
              Phone override <span className="text-neutral-600 normal-case tracking-normal">— optional, only if we don't have one on file</span>
            </label>
            <input
              type="tel"
              value={phoneOverride}
              onChange={(e) => setPhoneOverride(e.target.value)}
              placeholder="+1 555 123 4567"
              data-testid="thanks-sms-editor-phone"
              className="w-full rounded-lg bg-black/60 border border-white/10 focus:border-emerald-400/40 focus:outline-none px-3 py-2 text-sm text-white placeholder:text-neutral-600 tabular-nums"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              data-testid="thanks-sms-editor-force"
              className="accent-emerald-400"
            />
            <span className="text-xs text-neutral-400">
              Force resend
              <span className="text-neutral-600"> · bypasses the 30-day idempotency guard</span>
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/5 bg-black/40">
          <button
            onClick={onClose}
            className="text-xs text-neutral-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30 transition-colors"
            data-testid="thanks-sms-editor-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onSend({
                message: message.trim(),
                phone_override: phoneOverride.trim() || undefined,
                force,
              })
            }
            disabled={busy || message.trim().length < 10}
            data-testid="thanks-sms-editor-send"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400 hover:bg-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed text-black text-xs font-bold px-4 py-1.5 transition-colors"
          >
            <ChatCircleText size={12} weight="fill" />
            {busy ? "Sending…" : "Send text"}
          </button>
        </div>
      </div>
    </div>
  );
}


const BookingsStat = ({ label, value, accent, testid, spark, sparkTone, sparkLabel, anomaly }) => {
  const spike = !!(anomaly && anomaly.spike_detected);
  const map = {
    emerald: "text-emerald-300",
    red: "text-red-300",
    orange: "text-[#FF9764]",
    neutral: "text-white",
  };
  return (
    <div
      className={`rounded-lg border p-3 ${
        spike
          ? "border-red-500/60 bg-red-500/[0.08] ring-1 ring-red-500/40"
          : "border-white/10 bg-black/40"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
          {label}
        </div>
        {spike && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-200 text-[9px] font-bold uppercase tracking-wider"
            data-testid={`${testid}-spike-badge`}
            title={`Today ${anomaly.today} · avg prior 6d ${anomaly.mean_prior_6d} · threshold ${anomaly.threshold}`}
          >
            ⚠ Spike
          </span>
        )}
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <div className={`text-2xl font-semibold tabular-nums ${map[accent] || "text-white"}`}>{value}</div>
        {Array.isArray(spark) && spark.length > 1 && (
          <Sparkline
            values={spark}
            tone={sparkTone}
            testid={`${testid}-spark`}
            label={sparkLabel || label.toLowerCase()}
          />
        )}
      </div>
      {spike && (
        <div className="text-[10px] text-red-300 mt-1 font-medium" data-testid={`${testid}-spike-note`}>
          Spike detected · today {anomaly.today} vs {anomaly.mean_prior_6d} avg
        </div>
      )}
    </div>
  );
};

// Tiny 7-day SVG polyline. Auto-scales to the max value in the window
// with a floor of 1 so a run of zeros renders as a flat baseline instead
// of nothing. Baseline shown as a dashed grey line for context.
// Each data point has an invisible hit-target circle with a native
// <title> so the browser renders "Tue Aug 5 · 3 sent" on hover.
const Sparkline = ({ values, tone = "orange", testid, label = "sent" }) => {
  if (!Array.isArray(values) || values.length < 2) return null;
  const W = 68, H = 22, PAD = 2;
  const max = Math.max(1, ...values);
  const stepX = (W - PAD * 2) / (values.length - 1);
  const pts = values.map((v, i) => {
    const x = PAD + i * stepX;
    const y = H - PAD - (v / max) * (H - PAD * 2);
    return { x, y, v, i };
  });
  const polylinePts = pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const strokeMap = {
    orange: "#FF9764",
    red: "#F87171",
    emerald: "#6EE7B7",
  };
  const stroke = strokeMap[tone] || "#FF9764";
  // Day labels — index 0 = 6 days ago, index N-1 = today.
  const today = new Date();
  const fmt = (dayOffset) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (values.length - 1 - dayOffset));
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };
  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      data-testid={testid}
      className="shrink-0 overflow-visible"
      aria-label={`7-day trend, latest ${values[values.length - 1]}`}
    >
      {/* Baseline */}
      <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD}
            stroke="rgba(255,255,255,0.08)" strokeDasharray="2 2" strokeWidth="1" />
      <polyline
        points={polylinePts}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Invisible-but-hoverable hit-target per point. `<title>` renders
          as a native browser tooltip on hover — no JS state needed. */}
      {pts.map((p) => (
        <g key={p.i}>
          <circle cx={p.x} cy={p.y} r="4"
                  fill="transparent"
                  stroke="transparent"
                  style={{ cursor: "help" }}
                  data-testid={`${testid}-point-${p.i}`}>
            <title>{`${fmt(p.i)} · ${p.v} ${label}`}</title>
          </circle>
        </g>
      ))}
      {/* Endpoint marker for "here's now" */}
      <circle cx={pts[pts.length - 1].x} cy={pts[pts.length - 1].y} r="2" fill={stroke}
              pointerEvents="none" />
    </svg>
  );
};
